import React, { useState } from "react";
import { Box, Button, Card, CardContent, FormControl, TextField, Typography } from "@mui/material";
import { COLORS } from "../../shared/colors";
import { Controller, useForm } from "react-hook-form";
import { useUserRegistrationMutation } from "../../services/user";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import ROUTES from "../../routes";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const formSchema = yup.object().shape({
	name: yup.string().required("Name is required"),
	email: yup.string().required("Email is required").email("Email is invalid"),
	password: yup
		.string()
		.required("Password is required")
		.min(7, "Password must be at least 7 characters")
		.max(15, "Password cannot exceed more than 15 characters"),
	confirmPass: yup
		.string()
		.oneOf([yup.ref("password"), null], "Passwords must match")
		.required("Confirm password is required"),
});

const Register = ({ type = "user" }) => {
	const {
		reset,
		register,
		handleSubmit,
		control,
		formState: { errors },
		getValues,
	} = useForm({
		mode: "onTouched",
		resolver: yupResolver(formSchema),
	});

	const [disableSubmit, setDisableSubmit] = useState(false);
	const navigate = useNavigate();
	const [userRegistration] = useUserRegistrationMutation();

	const validatePasswordMatch = (value) => {
		const password = getValues("password"); // Get the value of the password field
		return value === password || "Passwords do not match";
	};

	// Constants for user types, consider moving these to a constants file if they are used in multiple places.
	const USER_TYPES = {
		user: "user",
		driver: "driver",
		org: "org",
	};

	const onSubmit = async (signUpData) => {
		setDisableSubmit(true);
		try {
			signUpData.type = USER_TYPES[type] || signUpData.type;

			const response = await userRegistration(signUpData);

			if (response.error) {
				toast.error(response.error.data.error);
			} else {
				reset();
				toast.success("Welcome aboard! Account Created Successfully!");
				navigate(ROUTES.PATHS.LOADS);
			}
		} catch (error) {
			console.error("Error during registration:", error);
			toast.error("An error occurred. Please try again later.");
		} finally {
			setDisableSubmit(false);
		}
	};

	return (
		<div
			style={{
				backgroundColor: COLORS.CULTURED,
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
			}}
		>
			<Box mb={3} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<img
					// eslint-disable-next-line no-undef
					src={require("../../assets/images/logo.png")}
					style={{
						width: "180px",
						height: "auto",
					}}
				/>
			</Box>
			<Card
				style={{
					border: "0.0625rem solid  #d1d7e0",
					boxShadow: "0 2px 18px rgba(0, 0, 0, 0.02)",
					borderRadius: " 0.5rem",
					width: "80%",
					margin: "0 auto",
				}}
				elevation={0}
			>
				<CardContent sx={{ minWidth: 378, padding: "30px 48px" }}>
					<Box mb={2}>
						<Typography sx={{ fontSize: 28 }} align="center" color={COLORS.SECONDARY}>
							Register
						</Typography>
						<Typography sx={{ fontSize: 16, color: COLORS.INDEPENDENCE }} align="center">
							Start Your Journey with Us
						</Typography>
					</Box>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridGap: "10px" }}>
							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="name"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											error={errors.name}
											id="filled-hidden-label-small"
											size="small"
											label="Name"
											helperText={errors.name?.message}
											InputProps={{ disableUnderline: true }}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="email"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											error={errors.email}
											id="filled-hidden-label-small"
											size="small"
											label="Email"
											helperText={errors.email?.message}
											InputProps={{ disableUnderline: true }}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="password"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											error={errors.password}
											id="password"
											type="password"
											size="small"
											label="Password"
											helperText={errors.password?.message}
											InputProps={{ disableUnderline: true }}
										/>
									)}
								/>
							</FormControl>

							<FormControl style={{ margin: "16px 0px" }}>
								<Controller
									name="confirmPass"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<TextField
											{...field}
											{...register("confirmPass", {
												required: true,
												validate: validatePasswordMatch,
											})}
											error={errors.confirmPass}
											id="confirmPass"
											type="password"
											label={"Confirm Password"}
											size="small"
											helperText={errors.confirmPass && "Please confirm your password"}
											InputProps={{ disableUnderline: true }}
										/>
									)}
								/>
							</FormControl>
						</div>

						<Box mt={2.5} mb={2}>
							<Button
								variant="contained"
								style={{ backgroundColor: COLORS.SECONDARY }}
								fullWidth
								type="submit"
								disabled={disableSubmit}
							>
								Sign Up
							</Button>
						</Box>
					</form>
					<Box mt={2.5} mb={2} sx={{ textAlign: "center" }}>
						Already have an account? <Link to="/login">Log in</Link>
					</Box>
				</CardContent>
			</Card>
		</div>
	);
};

export default Register;
